import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Code,
  GraduationCap,
  Music,
  FileText,
  ArrowRight,
  Calendar,
  ExternalLink,
  Mail,
  Gitlab,
  Github,
} from "lucide-react";
import data from "../data/software.json";

const HomePage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  };

  const latestExperience = data["Professional Experience"][0];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto"
      >
        {/* Hero Section */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h1 className="font-display font-bold text-5xl md:text-7xl text-secondary-900 mb-6">
            Rohit N. Saigal
          </h1>
          <p className="text-2xl md:text-3xl text-secondary-600 mb-8 max-w-4xl mx-auto">
            Senior Software Developer
          </p>
          <p className="text-lg text-secondary-500 max-w-3xl mx-auto leading-relaxed">
            Full-stack engineer with 6+ years of experience designing and
            deploying secure, high-scale systems in cloud environments.
            Passionate about solving complex engineering problems in meaningful
            domains such as health tech.
          </p>
        </motion.div>

        {/* Current Role Highlight */}
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8 mb-16"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center">
                {latestExperience.icon ? (
                  <img
                    src={latestExperience.icon}
                    alt={`${latestExperience.company} logo`}
                    className="w-12 h-12 object-contain"
                  />
                ) : (
                  <span className="text-secondary-600 font-bold text-lg">
                    {latestExperience.company.charAt(0)}
                  </span>
                )}
              </div>
              <div>
                <h3 className="font-display font-semibold text-xl text-secondary-900">
                  {latestExperience.title}
                </h3>
                <p className="text-secondary-600">{latestExperience.company}</p>
                <div className="flex items-center space-x-4 mt-1 text-sm text-secondary-500">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {formatDate(latestExperience.startDate)} -{" "}
                      {formatDate(latestExperience.endDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-secondary-500">Most Recent Role</p>
              <p className="font-semibold text-secondary-900">
                Full-Stack Leadership
              </p>
            </div>
          </div>
        </motion.div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* Experience Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/experience" className="block">
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-primary-500 group h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <Briefcase className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-secondary-900 group-hover:text-primary-600 transition-colors duration-200">
                      Experience
                    </h3>
                    <p className="text-secondary-500 text-sm">
                      Professional Journey
                    </p>
                  </div>
                </div>
                <p className="text-secondary-600 text-sm mb-4">
                  From Coupang to Cimpress, explore my roles in building
                  scalable systems and leading engineering teams.
                </p>
                <div className="flex items-center text-primary-600 group-hover:text-primary-700 transition-colors duration-200">
                  <span className="text-sm font-medium">View Timeline</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Skills Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/skills" className="block">
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-accent-500 group h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center group-hover:bg-accent-200 transition-colors duration-200">
                    <Code className="w-6 h-6 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-secondary-900 group-hover:text-accent-600 transition-colors duration-200">
                      Skills
                    </h3>
                    <p className="text-secondary-500 text-sm">
                      Technical Expertise
                    </p>
                  </div>
                </div>
                <p className="text-secondary-600 text-sm mb-4">
                  Languages, frameworks, cloud services, and tools I use to
                  build secure, scalable applications.
                </p>
                <div className="flex items-center text-accent-600 group-hover:text-accent-700 transition-colors duration-200">
                  <span className="text-sm font-medium">Explore Skills</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Resume Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/resume" className="block">
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-secondary-500 group h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center group-hover:bg-secondary-200 transition-colors duration-200">
                    <FileText className="w-6 h-6 text-secondary-600" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-secondary-900 group-hover:text-secondary-600 transition-colors duration-200">
                      Resume
                    </h3>
                    <p className="text-secondary-500 text-sm">
                      Professional Summary
                    </p>
                  </div>
                </div>
                <p className="text-secondary-600 text-sm mb-4">
                  Download my detailed resume with experience, projects, and
                  technical skills.
                </p>
                <div className="flex items-center text-secondary-600 group-hover:text-secondary-700 transition-colors duration-200">
                  <span className="text-sm font-medium">View Resume</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Education Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/education" className="block">
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-primary-500 group h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-200">
                    <GraduationCap className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-secondary-900 group-hover:text-primary-600 transition-colors duration-200">
                      Education
                    </h3>
                    <p className="text-secondary-500 text-sm">
                      Academic Background
                    </p>
                  </div>
                </div>
                <p className="text-secondary-600 text-sm mb-4">
                  My educational journey at the University of Illinois and how
                  it shaped my technical foundation.
                </p>
                <div className="flex items-center text-primary-600 group-hover:text-primary-700 transition-colors duration-200">
                  <span className="text-sm font-medium">Learn More</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Projects Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/projects" className="block">
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-accent-500 group h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center group-hover:bg-accent-200 transition-colors duration-200">
                    <Code className="w-6 h-6 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-secondary-900 group-hover:text-accent-600 transition-colors duration-200">
                      Projects
                    </h3>
                    <p className="text-secondary-500 text-sm">
                      Personal & Professional
                    </p>
                  </div>
                </div>
                <p className="text-secondary-600 text-sm mb-4">
                  From AI-powered apps to infrastructure modules, explore my
                  technical projects and contributions.
                </p>
                <div className="flex items-center text-accent-600 group-hover:text-accent-700 transition-colors duration-200">
                  <span className="text-sm font-medium">View Projects</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Music Card */}
          <motion.div
            variants={cardVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/music" className="block">
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 border-secondary-500 group h-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center group-hover:bg-secondary-200 transition-colors duration-200">
                    <Music className="w-6 h-6 text-secondary-600" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-secondary-900 group-hover:text-secondary-600 transition-colors duration-200">
                      Music
                    </h3>
                    <p className="text-secondary-500 text-sm">
                      Creative Pursuits
                    </p>
                  </div>
                </div>
                <p className="text-secondary-600 text-sm mb-4">
                  Discover my passion for music production and how it
                  complements my technical work.
                </p>
                <div className="flex items-center text-secondary-600 group-hover:text-secondary-700 transition-colors duration-200">
                  <span className="text-sm font-medium">Listen Now</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </div>
            </Link>
          </motion.div>
        </div>

        {/* Contact Section */}
        <motion.div variants={itemVariants} className="text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="font-display font-semibold text-2xl text-secondary-900 mb-4">
              Let's Connect
            </h3>
            <p className="text-secondary-600 mb-6 max-w-2xl mx-auto">
              I'm always interested in discussing new opportunities, especially
              in health tech and backend engineering. Feel free to reach out if
              you'd like to collaborate or just want to chat about technology.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:rohitnsaigal@gmail.com"
                className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <Mail className="w-5 h-5" />
                <span>Email Me</span>
              </a>
              <a
                href="https://www.linkedin.com/in/rohit-saigal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-secondary-600 hover:bg-secondary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <ExternalLink className="w-5 h-5" />
                <span>LinkedIn</span>
              </a>
              <a
                href="https://gitlab.com/rohitnsaigal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-secondary-600 hover:bg-secondary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <Gitlab className="w-5 h-5" />
                <span>GitLab</span>
              </a>
              <a
                href="https://github.com/rohitnsaigal"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-secondary-600 hover:bg-secondary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <Github className="w-5 h-5" />
                <span>GitHub</span>
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default HomePage;
